import React, { useEffect, useRef, useState } from 'react';
import Footer from '../components/Footer';
import { supabase } from '../supabase';
import './Home.css';

const WIXBASE = 'https://static.wixstatic.com/media/';
const PHOTOS = {
  hero:          WIXBASE + 'fb795d_c152d1073c0a4233b6a01a49689df334~mv2.webp',
  mission:       WIXBASE + 'fb795d_d4a631250f29491682e42932fb01c98b~mv2.jpg',
  mentorship:    WIXBASE + 'fb795d_f0be5a0206f04739af0ddc3e671edfc8~mv2.webp',
  audience:      WIXBASE + 'fb795d_771db96aa6814befb4282b4c8800cec1~mv2.png',
  companionship: WIXBASE + 'fb795d_a4464f1773fe4af093e859d853cde972~mv2.webp',
  accessible:    WIXBASE + 'fb795d_a91fa2ce8296486990ac966c92abed99~mv2.webp',
  cta1:          WIXBASE + 'fb795d_e4f28c9ed1634964889ab0733aa32165~mv2.webp',
  cta2:          WIXBASE + 'fb795d_ac767aec11824828a66654fe0c2982c9~mv2.jpg',
  chapter:       WIXBASE + 'fb795d_8d93821eaed7449caac68cf0b52cb9c0~mv2.jpg',
};

function useInView(ref, threshold = 0.12) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref, threshold]);
  return visible;
}

function FadeUp({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const visible = useInView(ref);
  return (
    <div ref={ref} className={`fade-up ${visible ? 'fade-up--visible' : ''} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

export default function Home({ navigate, showToast }) {
  const [stats, setStats] = useState([
    { label: 'Lessons Taught',   value: '1,300', suffix: '+' },
    { label: 'Nations Reached',  value: '31',    suffix: '' },
    { label: 'Partner Schools',  value: '94',    suffix: '' },
    { label: 'Students Reached', value: '500K',  suffix: '+' },
  ]);

  const offerings = [
    { num: '01', title: 'Mentorship',    img: PHOTOS.mentorship,    imgPos: 'left center',   desc: 'Music can seem complex, but becomes easy with the right guidance. We bring musicians from top high school ensembles to support you every step of the way.' },
    { num: '02', title: 'Audience',      img: PHOTOS.audience,      imgPos: 'right center',  desc: "Music is made to be performed. Whether it's for a competition or a passion project, our mentors provide an attentive, supportive ear for your next masterpiece." },
    { num: '03', title: 'Companionship', img: PHOTOS.companionship, imgPos: 'center top',    desc: 'Music brings people together. We build real friendships between people of different ages and backgrounds, creating connections that last a lifetime.' },
    { num: '04', title: 'Accessible',    img: PHOTOS.accessible,    imgPos: 'center top',    desc: 'Music is for everyone. Our program is entirely online and completely free, so anyone can get great support, no matter their financial situation.' },
  ];

  return (
    <div className="home">

      {/* ── HERO ── */}
      <section className="hero">
        <div className="hero-content">
          {/* Big logo */}
          <div className="hero-logo-wrap">
            <img src="/echo-logo.png" alt="ECHO" className="hero-logo-img" />
          </div>
          {/* Subheading */}
          <h1 className="hero-tagline">Share Your Sound</h1>
          <p className="hero-sub">
            Free music tutoring for students of all ages and backgrounds, run entirely online by passionate high school musicians from around the world.
          </p>
          <div className="hero-btns">
            <a href="https://bit.ly/echoscheduling" target="_blank" rel="noopener noreferrer" className="btn-primary">Schedule a Lesson</a>
            <button className="btn-outline" onClick={() => navigate('about')}>
              Our Story
            </button>
          </div>
        </div>

        {/* Right side photo + wave */}
        <div className="hero-right">
          <div className="hero-img-frame">
            <img src={PHOTOS.hero} alt="Echo music" className="hero-img" />
            <div className="hero-img-overlay" />
          </div>
          <div className="hero-quote-card">
            <div className="hero-soundwave">
              {[...Array(12)].map((_, i) => (
                <div key={i} className="wave-bar" style={{ animationDelay: `${i * 0.09}s` }} />
              ))}
            </div>
            <blockquote>
              "Music washes away from the soul the dust of everyday life."
            </blockquote>
            <cite>- Berthold Auerbach</cite>
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <div className="stats-bar">
        {stats.map((s, i) => (
          <FadeUp key={i} delay={i * 80} className="stat-item">
            <div className="stat-num">{s.value}<span>{s.suffix}</span></div>
            <div className="stat-label">{s.label}</div>
          </FadeUp>
        ))}
      </div>

      {/* ── MISSION ── */}
      <section className="mission-section">
        <FadeUp className="mission-text">
          <div className="section-tag">Our Mission</div>
          <h2 className="section-h2">Democratizing <em>music access</em> for marginalized communities</h2>
          <p>We are an international initiative passionate about bringing music to students everywhere. We seek to use our online platform to bring music education to students who need it most.</p>
          <p>ECHO is run entirely by high school students and provides free online tutoring to students of all ages. No experience needed, no sign-up fees.</p>
          <button className="btn-primary" onClick={() => navigate('about')} style={{ marginTop: '1.5rem' }}>
            Learn more →
          </button>
        </FadeUp>
        <FadeUp delay={120} className="mission-visual">
          <div className="mission-img-wrap">
            <img src={PHOTOS.mission} alt="Music" className="mission-img" />
          </div>
          <div className="mission-pillars">
            {['Mentorship','Global Reach','Free Forever','Community'].map((p, i) => (
              <div key={i} className="mission-pill">{p}</div>
            ))}
          </div>
        </FadeUp>
      </section>

      {/* ── OFFERINGS ── */}
      <section className="offerings-section">
        <FadeUp>
          <div className="section-tag">What We Offer</div>
          <h2 className="section-h2">Everything you need to <em>grow</em></h2>
        </FadeUp>
        <div className="offerings-grid">
          {offerings.map((o, i) => (
            <FadeUp key={i} delay={i * 100} className="offering-card">
              <div className="offering-img-wrap">
                <img src={o.img} alt={o.title} className="offering-img" style={{ objectPosition: o.imgPos }} />
                <div className="offering-img-overlay" />
              </div>
              <div className="offering-body">
                <div className="offering-num">{o.num}</div>
                <h3 className="offering-title">{o.title}</h3>
                <p className="offering-desc">{o.desc}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* ── MEDIA ── */}
      <section className="media-section">
        <FadeUp>
          <div className="section-tag">Featured Media</div>
          <h2 className="section-h2">
            Hear from <em>our community</em>
          </h2>
        </FadeUp>
        <div className="media-grid">
          <FadeUp delay={80} className="media-card">
            <div className="media-card-icon">▶</div>
            <h3>Podcast Episode</h3>
            <p>Co-founder Iris Cao joins Rose Tinted Ceilings to discuss how students can introduce initiatives with real community impact.</p>
            <a href="https://www.youtube.com/watch?v=LfEXqRxf6VY"
               target="_blank" rel="noopener noreferrer" className="btn-light">
              Watch on YouTube
            </a>
          </FadeUp>
          <FadeUp delay={160} className="media-card">
            <div className="media-card-icon">🎼</div>
            <h3>Student Recital</h3>
            <p>Listen to 7 incredible pieces performed by our ECHO community, showcasing our students' hard work and progress.</p>
            <a href="https://www.youtube.com/watch?v=vQE4shcgB00"
               target="_blank" rel="noopener noreferrer" className="btn-light">
              Watch on YouTube
            </a>
          </FadeUp>
        </div>
      </section>

      {/* ── CTA STRIP ── */}

      <section className="cta-strip">
        <FadeUp className="cta-block">
          <div className="cta-block-img-wrap">
            <img src={PHOTOS.cta1} alt="Schedule" className="cta-block-img" />
          </div>
          <div className="cta-block-body">
            <h3>Schedule a Lesson</h3>
            <p>Fill out our quick form and get matched with a tutor for your instrument. Completely free, completely online.</p>
            <a href="https://bit.ly/echoscheduling" target="_blank" rel="noopener noreferrer" className="btn-primary">Book Now</a>
          </div>
        </FadeUp>
        <FadeUp delay={100} className="cta-block">
          <div className="cta-block-img-wrap">
            <img src={PHOTOS.cta2} alt="Tutor" className="cta-block-img" />
          </div>
          <div className="cta-block-body">
            <h3>Become a Tutor</h3>
            <p>Are you a passionate high school musician? Join our global team of tutors and make a real difference in students' lives.</p>
            <a href="http://forms.gle/S8MaxaTmkPWTsgnV8" target="_blank" rel="noopener noreferrer" className="btn-primary">Apply Now</a>
          </div>
        </FadeUp>
        <FadeUp delay={200} className="cta-block">
          <div className="cta-block-img-wrap">
            <img src={PHOTOS.chapter} alt="Chapter" className="cta-block-img" />
          </div>
          <div className="cta-block-body">
            <h3>Start a Chapter</h3>
            <p>Join 10+ chapters across the globe and spread musical passion in your local community with full organizational support.</p>
            <a href="https://docs.google.com/forms/u/4/d/1ypHAM0JhnWBWJy_c62LQSxaP6UMIxAhLJtSYbaJsy18/edit"
               target="_blank" rel="noopener noreferrer" className="btn-primary">Apply Now</a>
          </div>
        </FadeUp>
      </section>

      <Footer navigate={navigate} />
    </div>
  );
}
